
<template v-if="gen">
  <tr
    class="generator-row"
    :class="{ 'has-errors': gen.errors?.length }"
  >
    <!-- Generator name + color dot -->
    <td :class="{ 'pl-10': indent }" @click="editGen(gen)">
      <span class="gen-label">
        <span
          class="generator-color-dot"
          :style="{ backgroundColor: genColor }"
          :title="String(genActionId)"
        ></span>

        <span class="generator-name">{{ gen.name }}</span>
          <v-chip
            size="x-small"
            color="secondary"
            variant="tonal"
          >
            Erzeuger
          </v-chip>
      </span>
    </td>

    <!-- Method text + error tooltip -->
    <td @click="editGen(gen)">
      {{ methodText }}
      <span v-if ="gen && gen.errors">
        <v-tooltip v-if="gen.errors?.length" location="bottom" color="error">
          <template #activator="{ props }">
            <v-icon v-bind="props" class="ml-2" color="error">mdi-alert</v-icon>
          </template>

          <div v-for="error in gen.errors" :key="error">
            {{ error }}<br />
          </div>
        </v-tooltip>
      </span>
    </td>

    <!-- Energiekosten are included in the parent action result -->
    <td
      class="text-right generator-empty-value"
      @click="editGen(gen)"
    >
      —
    </td>

    <!-- Einspeisung -->
    <td
      v-if="showFeedInColumn"
      class="text-right generator-empty-value"
      @click="editGen(gen)"
    >
      —
    </td>

    <!-- Wartungskosten -->
    <td
      class="text-right generator-empty-value"
      @click="editGen(gen)"
    >
      —
    </td>

    <!-- CO₂ -->
    <td
      class="text-right generator-empty-value"
      @click="editGen(gen)"
    >
      —
    </td>

    <!-- Invest -->
    <td
      class="text-right generator-empty-value"
      @click="editGen(gen)"
    >
      —
    </td>

    <!-- Energy values -->
    <td
      v-for="eid in energies"
      :key="'gen-energy-' + eid"
      class="text-right generator-empty-value"
      @click="editGen(gen)"
    >
      —
    </td>

    <!-- Buttons -->
    <td class="gen-cell text-center sticky-report-column">
      <div class="gen">
        <v-btn
          :disabled="(gen.errors?.length || 0) !== 0"
          :href="docxDownloadHref"
          color="primary"
          variant="text"
          size="small"
          density="comfortable"
          :ripple="false"
          icon
          title="Kurzbericht dieser Maßnahme als Word Export"
          @click.stop
        >
          <v-icon size="20">mdi-file-word</v-icon>
        </v-btn>

        <v-btn
          icon
          variant="text"
          size="small"
          density="comfortable"
          :ripple="false"
          title="Maßnahme bearbeiten"
          @click.stop="editGen(gen)"
        >
          <v-icon size="20">mdi-pencil</v-icon>
        </v-btn>
      </div>
    </td>

  </tr>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/projectStore'

const props = defineProps({
  gen: { type: Object, required: true },
  energies: { type: Array, required: true },
  indent: { type: Boolean, default: false },
  methodTexts: { type: Object, default: null },
  showPercentages: { type: Boolean, default: false },   // <-- NEW
  showFeedInColumn: { type: Boolean, default: false },
})

const router = useRouter()
const store = useProjectStore()





const docxDownloadHref = computed(
  () => `/api/v1/actions/${props.gen?.id}/action_report/`
)


const genActionId = computed(() => props.gen?.id)

const genColor = computed(() => {
  const id = genActionId.value
  return (id != null && store.genColors?.[id]) ? store.genColors[id] : '#888'
})

const methodText = computed(() => {
  if (!props.methodTexts) return ''
  const genType = props.gen?.type
  if (!genType) return ''
  return props.methodTexts[genType] || ''
})

function editGen(gen) {
  const actionId = gen?.id
  if (actionId != null) {
    router.push({ name: 'Action', params: { actionId } })
  }
}
</script>

<style scoped lang="scss">
.generator-row {
  cursor: pointer;

  color: rgba(var(--v-theme-on-surface), 0.82);
  background-color: rgba(var(--v-theme-secondary), 0.035);

  transition: background-color 160ms ease;
}

.generator-row:hover {
  background-color: rgba(var(--v-theme-secondary), 0.075);
}

.generator-row.has-errors {
  background-color: rgba(var(--v-theme-error), 0.035);
}

/* ========================================
   Generator identity
======================================== */

.gen-label {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;

  margin-left: 1.5rem;
}

.generator-color-dot {
  display: inline-block;
  flex-shrink: 0;

  width: 11px;
  height: 11px;
  border-radius: 999px;

  box-shadow: 0 0 0 2px rgb(var(--v-theme-surface));
}

.generator-name {
  max-width: 240px;
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.generator-empty-value {
  color: rgba(var(--v-theme-on-surface), 0.38);
}

/* ========================================
   Horizontally sticky generator column

   This cell remains visible while scrolling
   left/right, but moves normally up/down.
======================================== */

td.gen-cell.sticky-report-column {
  position: sticky !important;
  right: 0 !important;

  /* Explicitly disable vertical stickiness */
  top: auto !important;
  bottom: auto !important;

  /*
   * Lower than:
   * - sticky header: 40
   * - sticky footer: 30
   * - footer/right intersection: 35
   */
  z-index: 10 !important;

  width: 96px;
  min-width: 96px;
  max-width: 96px;

  padding-inline: 6px !important;
  vertical-align: middle;

  background:
    linear-gradient(
      rgba(var(--v-theme-secondary), 0.035),
      rgba(var(--v-theme-secondary), 0.035)
    ),
    rgb(var(--v-theme-surface)) !important;
}

/* Match generator-row hover color */
.generator-row:hover td.gen-cell.sticky-report-column {
  background:
    linear-gradient(
      rgba(var(--v-theme-secondary), 0.075),
      rgba(var(--v-theme-secondary), 0.075)
    ),
    rgb(var(--v-theme-surface)) !important;
}

/* Match generator error-row color */
.generator-row.has-errors td.gen-cell.sticky-report-column {
  background:
    linear-gradient(
      rgba(var(--v-theme-error), 0.035),
      rgba(var(--v-theme-error), 0.035)
    ),
    rgb(var(--v-theme-surface)) !important;
}

/* ========================================
   Generator action buttons
======================================== */

td.gen-cell {
  vertical-align: middle;
}

.gen-cell .gen {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;

  width: 100%;
}

.gen-cell .v-btn.v-btn--icon {
  width: 34px;
  min-width: 34px;
  height: 34px;

  padding: 0;
  border-radius: 999px;
}

.gen-cell .v-btn:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* ========================================
   Small screens
======================================== */

@media (max-width: 700px) {
  .gen-label {
    margin-left: 0.75rem;
  }

  .generator-name {
    max-width: 190px;
  }

  td.gen-cell.sticky-report-column {
    width: 88px;
    min-width: 88px;
    max-width: 88px;

    /*
     * Retain horizontal-only stickiness.
     */
    top: auto !important;
    bottom: auto !important;
  }

  .gen-cell .gen {
    gap: 0.15rem;
  }

  .gen-cell .v-btn.v-btn--icon {
    width: 32px;
    min-width: 32px;
    height: 32px;
  }
}
</style>